import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useInstagramFeed } from '../../hooks/useInstagramFeed';
import { SocialLinks } from '../landing/SocialLinks';

function getAppDomain(): string {
  if (typeof window === 'undefined') return 'https://myevent.np-productions.com';
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '/login';
  }
  return 'https://myevent.np-productions.com/login';
}

const NAV_LINKS = [
  { href: '#services', label: 'Services' },
  { href: '#about', label: 'About' },
  { href: '#instagram', label: 'Gallery' },
  { href: '#contact', label: 'Contact' },
];

export function PublicLayout() {
  const loginUrl = getAppDomain();
  const [menuOpen, setMenuOpen] = useState(false);
  const { feed } = useInstagramFeed();
  const instagramUrl = feed?.profileUrl ?? null;

  // Smooth-scroll anchor navigation just for the public site.
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = 'smooth';
    return () => {
      root.style.scrollBehavior = previous;
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <a href="#top" className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="NP Productions"
              className="h-10 w-10 rounded bg-gray-900 p-1"
            />
            <div className="leading-tight">
              <span className="text-lg font-bold text-gray-900">NP Productions</span>
              <br />
              <span className="text-xs text-gray-500">Events &amp; Production</span>
            </div>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm text-gray-600 hover:text-primary-600 transition-colors"
              >
                {link.label}
              </a>
            ))}
            <SocialLinks
              instagramUrl={instagramUrl}
              linkClassName="text-gray-500 hover:text-primary-600"
            />
            <a
              href={loginUrl}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              Login
            </a>
          </nav>

          {/* Mobile menu toggle */}
          <button
            type="button"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile nav panel */}
        {menuOpen && (
          <nav className="md:hidden border-t border-gray-100 bg-white px-4 py-3 space-y-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="block px-3 py-2 rounded-md text-base text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="flex items-center justify-between px-3 pt-2">
              <SocialLinks
                instagramUrl={instagramUrl}
                linkClassName="text-gray-500 hover:text-primary-600"
              />
              <a
                href={loginUrl}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
              >
                Login
              </a>
            </div>
          </nav>
        )}
      </header>

      <main id="top" className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
